import Image from "next/image";
import Link from "next/link";
import { ArrowRightCircle } from "lucide-react";
import { BlockProps } from "../BlocksManager/registry";

type VariantStyle = {
    card?: string;
    border?: string;
    bubble?: string;
    badge?: string;
    button?: string;
    titleHighlight?: string;

};

const variantStyles: { [key: string]: VariantStyle } = {
    bleu: {
        card: "bg-violet-light",
        border: "border-blue",
        bubble: "bg-blue",
        badge: "bg-blue",
        button: "bg-blue",
        titleHighlight: "bg-blue",

    },
    orange: {
        card: "bg-white",
        border: "border-orange",
        bubble: "bg-orange",
        badge: "bg-orange",
        button: "bg-orange",
        titleHighlight: "bg-orange",
    },

};

function getVariantStyle(variante?: string) {
    const key = variante?.toLowerCase?.().trim() || "bleu";
    return variantStyles[key] || variantStyles.bleu;
}

function renderMarkedRemise(nom: string) {
    const parts = nom.split(/(\[.*?\])/g);

    return parts.map((part, index) => {
        const isHighlighted = part.startsWith("[") && part.endsWith("]");

        if (isHighlighted) {
            const text = part.slice(1, -1);

            return (
                <span
                    key={index}
                    className="text-[24px] text-white md:text-[42px]"
                >
                    <br />
                    {text}
                    <br />
                </span>
            );
        }

        return <span key={index}>{part}</span>;
    });
}

function renderMarkedTitle(title: string, highlightClass: string) {
    const parts = title.split(/(\[.*?\])/g);

    return parts.map((part, index) => {
        const isHighlighted = part.startsWith("[") && part.endsWith("]");

        if (isHighlighted) {
            const text = part.slice(1, -1);

            return (
                <span
                    key={index}
                    className={`inline-block font-heading -rotate-1 my-2 uppercase tracking-widest px-3 py-1 text-white ${highlightClass}`}
                >
                    {text}
                </span>
            );
        }

        return <span key={index}>{part}</span>;
    });
}

function renderMarkedRecette(nom: string) {
    const parts = nom.split(/(\[.*?\])/g);
    return parts.map((part, index) => {
        const isHighlighted = part.startsWith("[") && part.endsWith("]");

        if (isHighlighted) {
            const text = part.slice(1, -1);

            return (
                <span
                    key={index}
                    className={`text-blue `}
                >
                    {text}
                </span>
            );
        }

        return <span key={index}>{part}</span>;
    });
}

function OfferButton({
    href,
    text,
    className,
}: {
    href?: string;
    text?: string;
    className: string;
}) {
    return (
        <Link
            href={href || "#"}
            className={`inline-flex w-fit items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-black transition  ${className}`}
        >
            {text}
            <ArrowRightCircle className="h-4 w-4" />
        </Link>
    );
}

type VariantBody = {
    bg: string;
};

const variantBodies: { [key: string]: VariantBody } = {
    blanc: {
        bg: "bg-white",
    },
    gris: {
        bg: "bg-violet-light",
    },
};


export default function NosOffres({ data }: BlockProps) {
    if (!data) return null;

    const offers = data?.offres || [];
    const firstOffer = offers[0];
    const secondOffer = offers[1];
    const remise = data?.remise;

    const firstStyle = getVariantStyle(firstOffer?.variante);
    const secondStyle = getVariantStyle(secondOffer?.variante);
    const bodyStyle = variantBodies[data?.variante?.toLowerCase()?.trim() || "blanc"]?.bg || "bg-white";

    const firstImage = (firstOffer?.image?.url);
    const firstIcon = (firstOffer?.icon?.url);
    const firstPlatsImage = (firstOffer?.plats?.image?.url);

    const secondImage = (secondOffer?.image?.url);
    const secondIcon = (secondOffer?.icon?.url);

    return (
        <section className={`py-14 ${bodyStyle}`}   >
            <div className="w-full container">
                <div className="mb-5 text-center">

                    <h2 className="font-display text-[36px] font-literata font-bold leading-tight text-black lg:text-[50px]">
                        {data?.titre}
                    </h2>
                    <p className="mt-3 text-[24px] lg:text-[28px] font-medium text-black">
                        {data?.sousTitre}
                    </p>
                </div>

                <div className="grid gap-5 xl:grid-cols-[1.02fr_1fr]">
                    {/* Carte gauche grande */}
                    {firstOffer && (
                        <article
                            className={`relative overflow-hidden rounded-[24px] border-4 p-6 md:p-8 ${firstStyle.card} ${firstStyle.border}`}
                        >

                            <div className={`absolute top-0 left-0 h-50 w-50 rounded-full blur-[162.7px] bg-blue`} />

                            {/* bulles */}

                            <div className={`hidden lg:block absolute top-[320px] right-[100px] h-[150px] w-[150px] rounded-full ${firstStyle.bubble}`} />
                            <div className={`hidden lg:block absolute top-[450px] right-[180px] h-[50px] w-[50px] rounded-full ${firstStyle.bubble}`} />
                            <div className={`hidden lg:block absolute bottom-[-34px] left-[300px] h-[70px] w-[70px] rounded-full ${firstStyle.bubble}`} />


                            <div className="relative z-10">
                                {/* Bloc offre */}
                                <div className="relative grid gap-6">
                                    <div className="pr-0 lg:pr-[220px]">
                                        {/* réserve l'espace pour l'image sur desktop */}
                                        {firstIcon && (
                                            <div className="mb-4 relative h-[60px] w-[60px] md:h-[75px] md:w-[75px]">
                                                <Image
                                                    src={firstIcon}
                                                    alt={firstOffer?.titre}
                                                    fill
                                                    className="object-contain"
                                                />
                                            </div>
                                        )}

                                        <h3 className="font-display text-[34px] lg:text-[50px] font-bold font-literata leading-none text-black">
                                            {renderMarkedTitle(firstOffer?.titre || "", firstStyle.titleHighlight)}
                                        </h3>

                                        <p className="mt-4 text-[18px] lg:text-[22px] leading-[1.25] text-black max-w-[360px]">
                                            {firstOffer?.description}
                                        </p>

                                        <div className="mt-4">
                                            <OfferButton
                                                href={firstOffer?.cta?.link}
                                                text={firstOffer?.cta?.text}
                                                className={firstStyle.button}
                                            />
                                        </div>
                                    </div>

                                    {firstImage && (
                                        <>
                                            {/* Mobile : image  */}
                                            <div className="lg:hidden  flex justify-center">
                                                <div className="relative aspect-square w-full max-w-[260px] sm:max-w-[300px]">
                                                    <div className="absolute inset-0 overflow-hidden rounded-full">
                                                        <Image
                                                            src={firstImage}
                                                            alt={firstOffer?.image?.alternativeText || firstOffer?.titre}
                                                            fill
                                                            className="object-cover"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Desktop : image absolue en haut à droite */}
                                            <div className="hidden lg:block absolute -top-40 -right-38 h-[440px] w-[440px]">
                                                <div className={`absolute inset-0 rounded-full bg-blue`} />
                                                <div className="absolute inset-0 overflow-hidden rounded-full">
                                                    <Image
                                                        src={firstImage}
                                                        alt={firstOffer?.image?.alternativeText || firstOffer?.titre}
                                                        fill
                                                        className="object-cover"
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Bloc plats */}
                                {firstOffer?.plats && (
                                    <div className="mt-8 grid items-end gap-6 lg:grid-cols-[1fr_220px]">
                                        <div>
                                            <h4 className="font-display text-[34px] lg:text-[50px] font-bold font-lobster leading-none text-black">
                                                {renderMarkedRecette(firstOffer?.plats?.titre || "")}
                                            </h4>

                                            <p className="mt-6 max-w-[420px] text-[22px] leading-[1.25] text-black">
                                                {firstOffer?.plats?.description}
                                            </p>

                                            <div className="mt-4">
                                                <OfferButton
                                                    href={firstOffer?.plats?.cta?.link}
                                                    text={firstOffer?.plats?.cta?.text}
                                                    className={firstStyle.button}
                                                />
                                            </div>
                                        </div>

                                        {firstPlatsImage && (
                                            <>

                                                {/* Mobile : image  */}
                                                <div className="lg:hidden  flex justify-center">
                                                    <div className="relative aspect-square w-full max-w-[260px] sm:max-w-[300px]">
                                                        <div className="absolute inset-0 overflow-hidden rounded-full">
                                                            <Image
                                                                src={firstPlatsImage}
                                                                alt={firstOffer?.plats?.image?.alternativeText || firstOffer?.plats?.titre}
                                                                fill
                                                                className="object-cover"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="hidden lg:block relative ml-auto h-[420px] w-[420px] overflow-hidden rounded-full">
                                                    <Image
                                                        src={firstPlatsImage}
                                                        alt={firstOffer?.plats?.image?.alternativeText || firstOffer?.plats?.titre}
                                                        fill
                                                        className="object-cover"
                                                    />
                                                </div>
                                            </>

                                        )}
                                    </div>
                                )}
                            </div>
                        </article>
                    )}

                    {/* Colonne droite */}
                    <div className="grid gap-5">
                        {/* Offre gourmet */}
                        {secondOffer && (
                            <article
                                className={`relative overflow-hidden rounded-[24px] border-4 p-6 md:p-8 ${secondStyle.card} ${secondStyle.border}`}
                            >

                                <div className={`absolute top-0 left-0 h-50 w-50 rounded-full blur-[162.7px] bg-orange`} />

                                <div className="relative z-10 grid gap-6 md:grid-cols-[1fr_230px]">
                                    <div>
                                        {secondIcon && (
                                            <div className="mb-4 relative h-[72px] w-[72px]">
                                                <Image
                                                    src={secondIcon}
                                                    alt={secondOffer?.icon?.alternativeText || secondOffer?.titre}
                                                    fill
                                                    className="object-contain"
                                                />
                                            </div>
                                        )}

                                        <h3 className="max-w-90 font-display text-[32px] lg:text-[50px] font-bold font-literata leading-none text-black">
                                            {renderMarkedTitle(secondOffer?.titre || "", secondStyle.titleHighlight)}
                                        </h3>

                                        <p className="mt-6 max-w-[340px] text-[22px] leading-[1.25] text-black">
                                            {secondOffer?.description}
                                        </p>

                                        <div className="mt-8">
                                            <OfferButton
                                                href={secondOffer?.cta?.link}
                                                text={secondOffer?.cta?.text}
                                                className={secondStyle.button}
                                            />
                                        </div>
                                    </div>

                                    {secondImage && (
                                        <>
                                            {/* Mobile : image  */}
                                            <div className="lg:hidden  flex justify-center">
                                                <div className="relative aspect-square w-full max-w-[260px] sm:max-w-[300px]">
                                                    <div className="absolute inset-0 overflow-hidden rounded-full">
                                                        <Image
                                                            src={secondImage}
                                                            alt={secondOffer?.image?.alternativeText || secondOffer?.titre}
                                                            fill
                                                            className="object-cover"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="hidden lg:block absolute -top-40 -right-38 h-[440px] w-[440px]">
                                                <div
                                                    className={`absolute inset-0 rounded-full bg-orange`}
                                                />
                                                <div className="absolute inset-0 overflow-hidden rounded-full">
                                                    <Image
                                                        src={secondImage}
                                                        alt={secondOffer?.image?.alternativeText || secondOffer?.titre}
                                                        fill
                                                        className="object-cover"
                                                    />
                                                </div>
                                            </div>
                                        </>

                                    )}
                                </div>
                            </article>
                        )}

                        {remise && (
                            <article className="relative overflow-hidden rounded-[32px] bg-violet px-6 py-8 text-white shadow-lg sm:px-8 sm:py-9">

                                {/* Cercle orange en haut à gauche */}
                                <div className="absolute -top-[34px] left-[52px] h-[120px] w-[120px] rounded-full bg-orange" />
                                {/* Petit cercle orange en bas */}
                                <div className="absolute -bottom-[28px] left-[43%] h-[74px] w-[74px] rounded-full bg-orange" />

                                <div className="relative z-10">

                                    {/* Ligne titre + badge */}
                                    <div className="flex items-start justify-between gap-3">
                                        <h3 className="font-literata text-[24px] font-bold leading-[1.1] text-white md:text-[36px] lg:text-[48px]">
                                            {remise?.titre}
                                        </h3>

                                        {/* Badge orange */}

                                        <div className="relative shrink-0 mt-1">
                                            <div className="absolute left-[5px] top-[6px] h-[90px] w-[90px] rotate-[10deg] rounded-[18px] bg-violet/50" />
                                            <div className="relative flex h-[130px] w-[130px] rotate-[10deg] items-center justify-center rounded-[200px] bg-orange px-2 text-center lg:h-[160px] lg:w-[200px] lg:rounded-[200px]">
                                                <p className="-rotate-[10deg] font-heading text-[16px] font-extrabold leading-[1.15] text-white whitespace-pre-line lg:text-[18px]">
                                                    {renderMarkedRemise(remise?.remise || "")}
                                                </p>
                                            </div>
                                        </div>


                                    </div>

                                    {/* Description */}
                                    <p className="mt-5 font-body text-[18px] leading-[1.4] text-white/95 sm:text-[17px] lg:mt-6 lg:text-[18px]">
                                        {remise?.description} <Link href={"/conditions-generales-de-vente"} className="font-bold underline">
                                            voir les conditions
                                        </Link>
                                    </p>

                                </div>
                            </article>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}